import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { auth } from '@/config/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { registerPushNotifications, refreshAndRegisterToken, forceRefreshAndRegister, updateUserLocation, shouldPromptForNotifications, areNotificationsEnabled } from '@/utils/pushNotifications';
import { toast } from 'sonner';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [notificationsActive, setNotificationsActive] = useState(false);
  const [userCity, setUserCityState] = useState(() => {
    return localStorage.getItem('mylocalgo_city') || null;
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
      // Auto-sync user profile to backend for admin dashboard
      if (user) {
        const city = localStorage.getItem('mylocalgo_city') || null;
        axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/${user.uid}/profile`, {
          display_name: user.displayName || null,
          email: user.email || null,
          phone_number: user.phoneNumber || null,
          photo_url: user.photoURL || null,
          city: city
        }).catch(() => {}); // Silent — never block auth flow
      }
    });
    return unsubscribe;
  }, []);

  // Register/refresh push notifications when user logs in
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    const initNotifications = async () => {
      // If permission already granted, register token using the full reliable flow
      // (deleteToken + getToken + save) — same as Re-sync, ensures fresh valid token
      if (areNotificationsEnabled()) {
        const token = await registerPushNotifications(currentUser.uid);
        if (token) {
          setNotificationsActive(true);
          updateUserLocation(currentUser.uid);
        }
        return;
      }
      
      // If permission not yet asked, show custom prompt after delay
      if (shouldPromptForNotifications()) {
        const dismissed = sessionStorage.getItem('notif_prompt_dismissed');
        if (!dismissed) {
          const timer = setTimeout(() => setShowNotificationPrompt(true), 2000);
          return () => clearTimeout(timer);
        }
      }
    };
    
    initNotifications();
  }, [currentUser]);

  const handleAllowNotifications = async () => {
    setShowNotificationPrompt(false);
    if (currentUser?.uid) {
      const token = await registerPushNotifications(currentUser.uid);
      if (token) {
        setNotificationsActive(true);
        updateUserLocation(currentUser.uid);
        toast.success('Notifications enabled! You\'ll get deal alerts nearby.');
      } else {
        toast.error('Could not enable notifications. Please try from Settings.');
      }
    }
  };

  const handleDismissNotifications = () => {
    setShowNotificationPrompt(false);
    sessionStorage.setItem('notif_prompt_dismissed', 'true');
  };

  // Force refresh notifications (for manual re-sync from Profile page)
  const forceRefreshNotifications = useCallback(async () => {
    if (!currentUser?.uid) return false;
    const token = await forceRefreshAndRegister(currentUser.uid);
    if (token) {
      setNotificationsActive(true);
      updateUserLocation(currentUser.uid);
      return true;
    }
    return false;
  }, [currentUser]);

  const setUserCity = (city) => {
    setUserCityState(city);
    if (city) {
      localStorage.setItem('mylocalgo_city', city);
    } else {
      localStorage.removeItem('mylocalgo_city');
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    user: currentUser,
    loading,
    userCity,
    setUserCity,
    logout,
    notificationsActive,
    forceRefreshNotifications,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      
      {/* Custom Notification Permission Prompt */}
      {showNotificationPrompt && (
        <div className="fixed bottom-24 left-4 right-4 z-[9999] animate-in slide-in-from-bottom" data-testid="notification-prompt">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 max-w-sm mx-auto">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-800 text-sm">Get Deal Alerts</h4>
                <p className="text-gray-500 text-xs mt-0.5">Get notified about deals near you and offers you'll love</p>
                <div className="flex space-x-2 mt-3">
                  <button
                    onClick={handleAllowNotifications}
                    className="px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition"
                    data-testid="allow-notifications-btn"
                  >
                    Allow
                  </button>
                  <button
                    onClick={handleDismissNotifications}
                    className="px-4 py-2 text-gray-500 text-xs font-medium hover:text-gray-700 transition"
                    data-testid="dismiss-notifications-btn"
                  >
                    Not now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
